import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { BillingService } from '../../../services/billing.service';
import { NotificationsService } from '../../../services/notifications.service';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ProductFormComponent } from './product-form.component';
import { StaticServiceFormComponent } from './static-service-form.component';
import { LocationProducts, NetsapiensSite, Product } from '@voice/utils';
import { SitesService } from '../../../services/sites.service';
import { CurrencyPipe, DatePipe, NgIf } from '@angular/common';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { WizardButtonsComponent } from './wizard-buttons.component';
import { ClientOnboardingService } from '../../../services/client-onboarding.service';
import { HostedSummariesResponse } from '../../../models/billing.model';
import { mapNsToProductCode } from '../shared/utils/ns-mapping.utils';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FormsModule } from '@angular/forms';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import {
  StaticServicesService,
  StaticServiceCode,
} from '../../../services/static-services.service';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { downloadCSV } from '../../../utils/file-manager';
import { CalendarModule } from 'primeng/calendar';
import { MatchedProduct } from './interfaces/matched-product.interface';
import { parseEffectiveDate } from './utils/product-form.utils';
import { PermissionsService, PermissionCheck } from '../../../services/permissions.service';

@Component({
  selector: 'app-client-onboarding-products-list',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    NgIf,
    CurrencyPipe,
    DatePipe,
    CardModule,
    WizardButtonsComponent,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    FormsModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule,
    CalendarModule,
  ],
  providers: [DialogService, ConfirmationService],
  templateUrl: './products-list.component.html',
})
export class ProductsListComponent implements OnInit {
  private readonly permissionsService = inject(PermissionsService);

  permissions: PermissionCheck = {
    canAdd: false,
    canEdit: false,
    canDelete: false,
    canView: false,
  };

  get canAdd(): boolean {
    return this.permissions.canAdd;
  }

  get canEdit(): boolean {
    return this.permissions.canEdit;
  }

  get canDelete(): boolean {
    return this.permissions.canDelete;
  }

  constructor(
    private readonly dialogService: DialogService,
    private readonly billingService: BillingService,
    private readonly sitesService: SitesService,
    private readonly clientOnboardingService: ClientOnboardingService,
    private readonly notificationService: NotificationsService,
    private readonly staticServicesService: StaticServicesService,
    private readonly confirmationService: ConfirmationService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  @Input()
  onBoardingId?: number;

  @Input()
  domain?: string;

  @Input()
  items?: LocationProducts[];

  products?: Product[];
  sites?: NetsapiensSite[];
  hostedSummaries?: HostedSummariesResponse;
  matchedProducts: MatchedProduct[] = [];
  editingProduct: MatchedProduct | null = null;
  availableStaticServiceCodes: StaticServiceCode[] = [];
  private activeLocationNames: string[] = [];
  private savedProducts: MatchedProduct[] = [];
  hasUnsavedChanges = false;
  hasSavedData = false;
  loading = false;
  globalEffectiveDate?: Date;
  savedGlobalEffectiveDate?: Date;

  @Output()
  productsReady: EventEmitter<string> = new EventEmitter<string>();

  trackByProductId(index: number, product: MatchedProduct): string {
    return product.id || `${product.site}-${product.product}-${index}`;
  }

  private async loadPermissions(): Promise<void> {
    this.permissions = await this.permissionsService.checkEntityPermissions('billing');
  }

  async ngOnInit(): Promise<void> {
    await this.loadPermissions();
    if (!this.items) {
      this.items = [];
    }
    if (this.onBoardingId) {
      this.clientOnboardingService.find(this.onBoardingId).subscribe({
        next: (record) => {
          this.activeLocationNames =
            record.locations?.map((location) => location.companyName).filter(Boolean) ?? [];
        },
        error: (err) => {
          this.notificationService.error(err);
        },
      });
    }
    if (this.onBoardingId && this.items && this.items.length > 0) {
      this.hasSavedData = true;
    }
    if (this.domain) {
      this.loading = true;
      this.billingService.getProducts().subscribe({
        next: (products) => {
          this.products = products;
          if (this.hasSavedData) {
            this.loadSavedProducts();
          } else {
            this.loadFromHostedSummaries();
          }
        },
        error: (err) => {
          this.loading = false;
          this.notificationService.error(err);
        },
      });
      this.sitesService.getSitesInDomain(this.domain).subscribe({
        next: (sites) => (this.sites = sites),
        error: (err) => this.notificationService.error(err),
      });
      if (!this.hasSavedData) {
        this.loadFromHostedSummaries();
      }
      this.staticServicesService.getStaticServiceCodes().subscribe({
        next: (codes) => (this.availableStaticServiceCodes = codes),
        error: (err) =>
          console.error('Error loading static service codes:', err),
      });
    }
  }

  private loadFromHostedSummaries(): void {
    if (this.domain) {
      this.billingService.getHostedSummaries([this.domain]).subscribe({
        next: (data) => {
          this.hostedSummaries = data;
          this.matchProductsWithServices();
        },
        error: (err) => this.notificationService.error(err),
      });
    }
  }

  private loadSavedProducts(): void {
    if (!this.items || this.items.length === 0) {
      return;
    }

    this.matchedProducts = [];
    const effectiveDates = new Set<string>();

    this.items.forEach((locationProduct) => {
      const products = locationProduct.products;
      if (products && products.length > 0) {
        products.forEach((product) => {
          const matchedProduct = this.createMatchedProductFromProduct(
            locationProduct.location || '',
            product
          );
          this.matchedProducts.push(matchedProduct);

          if (matchedProduct.effectiveDate) {
            const dateStr = new Date(matchedProduct.effectiveDate).toISOString().split('T')[0];
            effectiveDates.add(dateStr);
          }
        });
      }
    });

    if (effectiveDates.size === 1 && !this.globalEffectiveDate) {
      const commonDate = Array.from(effectiveDates)[0];
      const productsWithCommonDate = this.matchedProducts.filter(
        (p) => p.effectiveDate &&
        new Date(p.effectiveDate).toISOString().split('T')[0] === commonDate
      );

      if (productsWithCommonDate.length === this.matchedProducts.length) {
        this.globalEffectiveDate = new Date(commonDate);
      }
    }

    this.savedProducts = JSON.parse(JSON.stringify(this.matchedProducts));
    this.savedGlobalEffectiveDate = this.globalEffectiveDate
      ? new Date(this.globalEffectiveDate)
      : undefined;

    if (!this.savedGlobalEffectiveDate && effectiveDates.size === 1) {
      const commonDate = Array.from(effectiveDates)[0];
      this.savedGlobalEffectiveDate = new Date(commonDate);
    }

    this.hasUnsavedChanges = false;
    this.loading = false;
  }

  private createMatchedProductFromProduct(
    location: string,
    product: Product
  ): MatchedProduct {
    return {
      id: `${location}-${product.code || ''}-${Date.now()}-${Math.random()}`,
      site: location,
      product: product.code || '',
      description: product.description || '-',
      rate: product.rate,
      quantity: product.quantity || 1,
      isStatic: false,
      effectiveDate: parseEffectiveDate(product.effectiveDate),
    };
  }

  refreshData() {
    if (this.hasUnsavedChanges) {
      this.confirmationService.confirm({
        message:
          'You have unsaved changes. Reloading will overwrite your changes. Are you sure you want to continue?',
        header: 'Confirm Reload',
        icon: 'pi pi-exclamation-triangle',
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-secondary',
        accept: () => {
          this.performRefresh();
        },
      });
    } else {
      this.performRefresh();
    }
  }

  private performRefresh(): void {
    if (this.domain) {
      this.billingService.getHostedSummaries([this.domain]).subscribe({
        next: (data) => {
          this.hostedSummaries = data;
          this.matchedProducts = [];
          this.matchProductsWithServices();
        },
        error: (err) => this.notificationService.error(err),
      });
    }
  }

  discardChanges(): void {
    if (this.hasUnsavedChanges && this.hasSavedData) {
      this.confirmationService.confirm({
        message:
          'Are you sure you want to discard all unsaved changes? This action cannot be undone.',
        header: 'Confirm Discard',
        icon: 'pi pi-exclamation-triangle',
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-secondary',
        accept: () => {
          this.matchedProducts = JSON.parse(JSON.stringify(this.savedProducts));
          this.globalEffectiveDate = this.savedGlobalEffectiveDate
            ? new Date(this.savedGlobalEffectiveDate)
            : undefined;
          this.hasUnsavedChanges = false;
          this.editingProduct = null;
        },
      });
    }
  }

  private matchProductsWithServices(): void {
    if (!this.hostedSummaries?.rows || !this.products) {
      this.matchedProducts = [];
      this.checkForChanges();
      return;
    }

    const newMatchedProducts: MatchedProduct[] = [];

    this.hostedSummaries.rows.forEach((service) => {
      if (!service.SITE || service.SITE.trim() === '') {
        return;
      }

      if (
        this.activeLocationNames.length > 0 &&
        !this.activeLocationNames.includes(service.SITE)
      ) {
        return;
      }

      const productCode = mapNsToProductCode(
        service.SERVICE_TYPE,
        service.SCOPE
      );

      if (!productCode) {
        return;
      }

      const product = this.products!.find((p) => p.code === productCode);
      if (product && product.code) {
        newMatchedProducts.push({
          id: `${service.SITE}-${product.code}-${Date.now()}-${Math.random()}`,
          site: service.SITE,
          product: product.code,
          description: product.description || '-',
          rate: product.rate,
          quantity: service.QUANTITY || 1,
          isStatic: false,
          effectiveDate: this.globalEffectiveDate,
        });
      }
    });

    if (this.matchedProducts.length === 0 && !this.hasSavedData) {
          this.matchedProducts = newMatchedProducts;
          this.savedProducts = JSON.parse(JSON.stringify(newMatchedProducts));
          this.savedGlobalEffectiveDate = this.globalEffectiveDate
            ? new Date(this.globalEffectiveDate)
            : undefined;
    }
    this.checkForChanges();
    this.loading = false;
  }

  private checkForChanges(): void {
    const currentProductsStr = JSON.stringify(
      this.matchedProducts.map((p) => ({
        site: p.site,
        product: p.product,
        description: p.description,
        rate: p.rate,
        quantity: p.quantity,
        isStatic: p.isStatic,
        effectiveDate: p.effectiveDate,
      }))
    );
    const savedProductsStr = JSON.stringify(
      this.savedProducts.map((p) => ({
        site: p.site,
        product: p.product,
        description: p.description,
        rate: p.rate,
        quantity: p.quantity,
        isStatic: p.isStatic,
        effectiveDate: p.effectiveDate,
      }))
    );
    const globalDateChanged =
      JSON.stringify(this.globalEffectiveDate) !==
      JSON.stringify(this.savedGlobalEffectiveDate);
    this.hasUnsavedChanges = currentProductsStr !== savedProductsStr || globalDateChanged;
  }

  onGlobalEffectiveDateChange(): void {
    if (!this.globalEffectiveDate) {
      const savedDateStr = this.savedGlobalEffectiveDate
        ? this.normalizeDateString(this.savedGlobalEffectiveDate)
        : null;

      if (savedDateStr) {
        this.matchedProducts = this.matchedProducts.map((product) => {
          if (this.hasIndividualEffectiveDateEdit(product)) {
            return { ...product };
          }

          const productDateStr = product.effectiveDate
            ? this.normalizeDateString(product.effectiveDate)
            : null;
          if (productDateStr === savedDateStr) {
            return { ...product, effectiveDate: undefined };
          }
          return { ...product };
        });
        this.cdr.detectChanges();
      }
      this.checkForChanges();
      return;
    }

    const newDate = new Date(this.globalEffectiveDate);
    const newDateCopy = new Date(newDate.getTime());

    this.matchedProducts = this.matchedProducts.map((product) => {
      if (this.hasIndividualEffectiveDateEdit(product)) {
        return { ...product };
      }

      return { ...product, effectiveDate: new Date(newDateCopy.getTime()) };
    });

    this.checkForChanges();
    this.cdr.detectChanges();
  }

  private hasIndividualEffectiveDateEdit(product: MatchedProduct): boolean {
    if (this.savedProducts.length === 0) {
      return false;
    }

    const savedProduct = this.savedProducts.find((sp) => sp.id === product.id);
    if (!savedProduct) {
      return false;
    }

    const currentDateStr = product.effectiveDate
      ? this.normalizeDateString(product.effectiveDate)
      : null;
    const savedDateStr = savedProduct.effectiveDate
      ? this.normalizeDateString(savedProduct.effectiveDate)
      : null;

    if (currentDateStr === null && savedDateStr === null) {
      return false;
    }

    return currentDateStr !== savedDateStr;
  }

  private normalizeDateString(date: Date | string | undefined): string {
    if (!date) {
      return '';
    }
    try {
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) {
        return '';
      }
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  }

  openProductForm(): void {
    if (!this.canAdd) return;
    if (!this.products || this.products.length === 0) {
      this.notificationService.error('Products not loaded yet');
      return;
    }

    const nonStaticProducts = this.products.filter(
      (p) => !this.availableStaticServiceCodes.some((sc) => sc.code === p.code)
    );

    const config: DynamicDialogConfig = {
      data: { products: nonStaticProducts, sites: this.sites },
      header: 'Add Product',
      modal: true,
      closable: true,
      width: '700px',
      contentStyle: {
        overflow: 'auto',
        maxHeight: '80vh',
        padding: '1.5rem',
      },
    };
    const dialogRef = this.dialogService.open(ProductFormComponent, config);

    dialogRef.onClose.subscribe({
      next: (data: LocationProducts | null) => {
        if (data && data.products && data.products.length > 0) {
          this.addProductsToMatchedList(data.location || '', data.products, false);
          this.checkForChanges();
        }
      },
    });
  }

  openAddStaticService(): void {
    if (!this.canAdd) return;
    if (!this.sites || this.sites.length === 0) {
      this.notificationService.error('Sites not loaded yet');
      return;
    }

    const config: DynamicDialogConfig = {
      data: {
        sites: this.sites,
      },
      header: 'Add Static Service',
      modal: true,
      closable: true,
      width: '700px',
      contentStyle: {
        overflow: 'auto',
        maxHeight: '80vh',
        padding: '1.5rem',
      },
    };
    const dialogRef = this.dialogService.open(StaticServiceFormComponent, config);

    dialogRef.onClose.subscribe({
      next: (data: LocationProducts | null) => {
        if (data && data.products && data.products.length > 0) {
          this.addProductsToMatchedList(data.location || '', data.products, true);
          this.checkForChanges();
        }
      },
    });
  }

  editProduct(product: MatchedProduct): void {
    if (!this.canEdit) return;
    this.editingProduct = { ...product };
  }

  saveProduct(product: MatchedProduct): void {
    if (!this.canEdit) return;
    if (this.editingProduct && product.id === this.editingProduct.id) {
      product.rate = this.editingProduct.rate;
      product.quantity = this.editingProduct.quantity || 1;
      product.effectiveDate = this.editingProduct.effectiveDate;
      this.editingProduct = null;
      this.checkForChanges();
    }
  }

  cancelEdit() {
    this.editingProduct = null;
  }

  removeProductById(idOrProduct: string | MatchedProduct): void {
    if (!this.canDelete) return;
    if (typeof idOrProduct === 'string') {
      const index = this.matchedProducts.findIndex((p) => p.id === idOrProduct);
      if (index > -1) {
        this.matchedProducts.splice(index, 1);
        this.checkForChanges();
        return;
      }
    }
    this.removeProduct(idOrProduct as MatchedProduct);
  }

  removeProduct(product: MatchedProduct) {
    if (!product) {
      return;
    }

    let index = -1;
    if (product.id) {
      index = this.matchedProducts.findIndex((p) => p.id === product.id);
    }

    if (index === -1) {
      index = this.matchedProducts.findIndex(
        (p) =>
          p.site === product.site &&
          p.product === product.product &&
          p.description === product.description &&
          (p.rate === product.rate ||
            (p.rate === undefined && product.rate === undefined)) &&
          (p.quantity === product.quantity ||
            (p.quantity === undefined && product.quantity === undefined))
      );
    }

    if (index === -1) {
      index = this.matchedProducts.indexOf(product);
    }

    if (index > -1) {
      this.matchedProducts.splice(index, 1);
      this.checkForChanges();
    } else {
      console.error('removeProduct: product not found in matchedProducts', {
        productId: product.id,
        productSite: product.site,
        productCode: product.product,
        matchedProductsLength: this.matchedProducts.length,
        matchedProducts: this.matchedProducts.map((p) => ({
          id: p.id,
          site: p.site,
          product: p.product,
        })),
      });
    }
  }

  handleProduct(record?: LocationProducts) {
    const config: DynamicDialogConfig = {
      data: { record, products: this.products, sites: this.sites },
      header: 'Products selection',
      modal: true,
      closable: true,
      width: `${window.innerWidth * 0.75}px`,
      contentStyle: {
        'overflow-y': 'auto',
      },
    };
    const dialogRef = this.dialogService.open(ProductFormComponent, config);

    dialogRef.onClose.subscribe({
      next: (data) => {
        if (data && this.items) {
          if (record) {
            this.items.splice(this.items.indexOf(record), 1, data);
          } else {
            this.items.push(data);
          }
        }
      },
    });
  }

  private addProductsToMatchedList(
    location: string,
    products: Product[],
    isStatic: boolean
  ): void {
    products.forEach((product) => {
      const effectiveDate = parseEffectiveDate(product.effectiveDate) ||
        this.globalEffectiveDate;
      const matchedProduct: MatchedProduct = {
        id: `${location}-${product.code || ''}-${Date.now()}-${Math.random()}`,
        site: location,
        product: product.code || '',
        description: product.description || '-',
        rate: product.rate,
        quantity: product.quantity || 1,
        isStatic,
        effectiveDate,
      };
      this.matchedProducts.push(matchedProduct);
    });
  }

  private convertMatchedProductsToLocationProducts(): LocationProducts[] {
    const locationProductsMap = new Map<string, Product[]>();

    this.matchedProducts.forEach((matched) => {
      const product = this.createProductFromMatched(matched);
      const location = matched.site;

      if (!locationProductsMap.has(location)) {
        locationProductsMap.set(location, []);
      }
      locationProductsMap.get(location)!.push(product);
    });

    return Array.from(locationProductsMap.entries()).map(([location, products]) => ({
      location,
      products,
    }));
  }

  private createProductFromMatched(matched: MatchedProduct): Product {
    const product: Product = {
      code: matched.product,
      description: matched.description,
      rate: matched.rate,
      quantity: matched.quantity || 1,
      effectiveDate: matched.effectiveDate || this.globalEffectiveDate,
    };

    const productFromList = this.products?.find((p) => p.code === matched.product);
    if (productFromList?.id) {
      product.id = productFromList.id;
    }

    return product;
  }

  saveProducts(silent = false): void {
    if (this.onBoardingId && this.matchedProducts.length > 0) {
      const locationProducts = this.convertMatchedProductsToLocationProducts();
      this.clientOnboardingService
        .saveProducts(this.onBoardingId, locationProducts)
        .subscribe({
          next: () => {
            this.savedProducts = JSON.parse(
              JSON.stringify(this.matchedProducts)
            );
            this.savedGlobalEffectiveDate = this.globalEffectiveDate
              ? new Date(this.globalEffectiveDate)
              : undefined;
            this.hasUnsavedChanges = false;
            this.hasSavedData = true;
            if (!silent) {
              this.notificationService.success('Products saved successfully');
            }
          },
          error: (err) => {
            this.notificationService.error('Failed to save products').then();
            console.error('Error saving products:', err);
          },
        });
    }
  }

  handleCommonButtonClicked(event: string) {
    if (this.onBoardingId) {
      if (this.matchedProducts.length === 0) {
        this.productsReady.emit(event);
      } else {
        if (this.hasUnsavedChanges || !this.hasSavedData) {
          const locationProducts =
            this.convertMatchedProductsToLocationProducts();
          this.clientOnboardingService
            .saveProducts(this.onBoardingId, locationProducts)
            .subscribe({
              next: () => {
                this.savedProducts = JSON.parse(
                  JSON.stringify(this.matchedProducts)
                );
                this.savedGlobalEffectiveDate = this.globalEffectiveDate
                  ? new Date(this.globalEffectiveDate)
                  : undefined;
                this.hasUnsavedChanges = false;
                this.hasSavedData = true;
                this.productsReady.emit(event);
              },
              error: (err) => {
                this.notificationService
                  .error('Failed to save products')
                  .then();
                console.error('Error saving products:', err);
              },
            });
        } else {
          this.productsReady.emit(event);
        }
      }
    } else {
      this.notificationService.error('No onboarding record selected').then();
    }
  }

  exportProducts(): void {
    if (!this.matchedProducts || this.matchedProducts.length === 0) {
      this.notificationService.error('No products to export');
      return;
    }

    const keys = ['site', 'product', 'description', 'rate', 'quantity', 'isStatic'];

    const exportData = this.matchedProducts.map((p) => ({
      site: p.site || '',
      product: p.product || '',
      description: p.description || '',
      rate: p.rate !== undefined ? p.rate : '',
      quantity: p.quantity !== undefined ? p.quantity : '',
      isStatic: p.isStatic ? 'Yes' : 'No',
    }));

    const fileName = `products_${this.domain || 'export'}`;
    downloadCSV(fileName, keys, exportData);
  }
}
